import type { ValuesOf } from "common/utils/types";

/**
 * Per-option-click performance tracker.
 *
 * Mirrors {@link ShowroomPerformance} but is scoped to a single click cycle (allocated
 * via {@link OptionSelectionPerformance.startCycle}) instead of finalizing once per
 * plan-load. Stage names are aligned with the first-load `timeTo3dInteractive` sub-stages
 * so dashboards can compare apples-to-apples. Additional diagnostic stages
 * isolate main-thread work between an option click and worker handoff.
 */

const optionSelectionStages = [
  "applyOptionChoices", // Click handler entry to optionChoices state applied (cascade rules included)
  "optionClickToWorkerPost", // Click handler entry through posting the choices to the generation worker
  "prepareShowroomOptionChoices", // Merge choices and, on the legacy path, apply rules and sanitization
  "deriveShowroomOptions", // Rebuild the option catalog consumed by the Showroom UI
  "prepareShowroomDocForGeneration", // Apply plan, elevation, and option-template overrides
  "computeShowroomSetToDefault", // Evaluate force-locked choices before generation
  "workerPostMessage", // Synchronous structured-clone and postMessage cost
  "generateBuilding", // Choices applied to building/GLTF ready (mode-agnostic on the wire)
  "render3dViewer", // Building/GLTF ready to first frame painted
  "optionTimeTo3dInteractive", // Click to first frame painted (mirrors timeTo3dInteractive)
] as const;
export type OptionSelectionStage = ValuesOf<typeof optionSelectionStages>;

export type OptionSelectionMode = "main" | "worker" | "server";

type CycleState = {
  id: number;
  optionSetId: string;
  optionSetName: string;
  optionId: string;
  optionName: string;
  mode: OptionSelectionMode;
  marks: Set<string>;
};

export type OptionSelectionReport = Record<OptionSelectionStage, number> & {
  optionSetId: string;
  optionSetName: string;
  optionId: string;
  optionName: string;
  mode: OptionSelectionMode;
};

export class OptionSelectionPerformance {
  activeCycle: CycleState | null = null;
  private nextId: number = 1;

  private startMarkName(cycleId: number, stage: OptionSelectionStage): string {
    return `start-optionSelection-${cycleId}-${stage}`;
  }
  private endMarkName(cycleId: number, stage: OptionSelectionStage): string {
    return `end-optionSelection-${cycleId}-${stage}`;
  }
  private measureName(cycleId: number, stage: OptionSelectionStage): string {
    return `measure-optionSelection-${cycleId}-${stage}`;
  }

  startCycle(optionSetId: string, optionSetName: string, optionId: string, optionName: string): number {
    if (this.activeCycle) {
      this.dispose(this.activeCycle.id);
    }
    const id = this.nextId++;
    this.activeCycle = {
      id,
      optionSetId,
      optionSetName,
      optionId,
      optionName,
      mode: "main",
      marks: new Set(),
    };
    return id;
  }

  setMode(cycleId: number, mode: OptionSelectionMode): void {
    if (this.activeCycle?.id === cycleId) {
      this.activeCycle.mode = mode;
    }
  }

  start(cycleId: number, stage: OptionSelectionStage): void {
    const cycle = this.activeCycle;
    if (!cycle || cycle.id !== cycleId) {
      return;
    }
    const mark = this.startMarkName(cycleId, stage);
    if (!cycle.marks.has(mark)) {
      performance.mark(mark);
      cycle.marks.add(mark);
    }
  }

  end(cycleId: number, stage: OptionSelectionStage): void {
    const cycle = this.activeCycle;
    if (!cycle || cycle.id !== cycleId) {
      return;
    }
    const startMark = this.startMarkName(cycleId, stage);
    const endMark = this.endMarkName(cycleId, stage);
    const measure = this.measureName(cycleId, stage);
    if (!cycle.marks.has(startMark) || cycle.marks.has(endMark)) {
      return;
    }
    performance.mark(endMark);
    cycle.marks.add(endMark);
    try {
      performance.measure(measure, startMark, endMark);
    } catch {
      // start mark may have been cleared; ignore
    }
  }

  report(cycleId: number): OptionSelectionReport | null {
    const cycle = this.activeCycle;
    if (!cycle || cycle.id !== cycleId) {
      return null;
    }
    const durations = {} as Record<OptionSelectionStage, number>;
    for (const stage of optionSelectionStages) {
      const entries = performance.getEntriesByName(this.measureName(cycleId, stage));
      durations[stage] = entries.length > 0 ? entries[0].duration : 0;
    }
    return {
      ...durations,
      optionSetId: cycle.optionSetId,
      optionSetName: cycle.optionSetName,
      optionId: cycle.optionId,
      optionName: cycle.optionName,
      mode: cycle.mode,
    };
  }

  dispose(cycleId: number): void {
    const cycle = this.activeCycle;
    if (!cycle || cycle.id !== cycleId) {
      return;
    }
    for (const stage of optionSelectionStages) {
      performance.clearMarks(this.startMarkName(cycleId, stage));
      performance.clearMarks(this.endMarkName(cycleId, stage));
      performance.clearMeasures(this.measureName(cycleId, stage));
    }
    this.activeCycle = null;
  }

  reset(): void {
    if (this.activeCycle) {
      this.dispose(this.activeCycle.id);
    }
  }
}

const singleton = new OptionSelectionPerformance();

/**
 * Access the option-selection performance singleton via callback.
 *
 * `requires` is a truthiness gate, not a React deps array — it gates execution, it does
 * not detect changes. Idempotency is per-cycle (the `marks` Set lives on `activeCycle`),
 * so a gated call from a render body WILL plant a fresh mark on the first render after
 * `startCycle()`. Plant per-cycle marks at deterministic call sites instead.
 */
export function optionSelectionPerformance(
  callback: (perf: OptionSelectionPerformance) => void,
  requires: unknown[] = [],
): void {
  if (!requires.length || requires.every((value) => !!value)) {
    callback(singleton);
  }
}

optionSelectionPerformance.getActiveCycleId = (): number | null => singleton.activeCycle?.id ?? null;

/** Measure synchronous work against the active option-selection cycle, if one exists. */
export function measureActiveOptionSelectionStage<T>(stage: OptionSelectionStage, action: () => T): T {
  const cycleId = singleton.activeCycle?.id;
  if (cycleId === undefined) {
    return action();
  }

  singleton.start(cycleId, stage);
  try {
    return action();
  } finally {
    singleton.end(cycleId, stage);
  }
}
